// @charitystack/imports-contracts — the shared FE↔BE contract for the
// Dashboard Import Tool (PE-1584).
//
// Schemas only. See this package's CLAUDE.md for why this is a schemas-only
// contract rather than a tRPC router-type re-export (short version: the
// dashboard's legacy `moduleResolution: "node"` can't resolve a backend
// package's internal subpath exports, so both shipped FE↔BE integrations in
// this monorepo type their tRPC proxy as `any` and import schemas directly —
// marketing.ts and dashboardAssistantHistory.ts).

import { z } from 'zod';

import { importJobSchema, importJobStatusSchema, importJobTypeSchema } from './import-job.js';
import {
  attestationDefaultSchema,
  columnTargetSchema,
  sourceFileHintSchema,
} from './fields.js';
// PE-1899 — the mapping/validation procedure I/O below reuses these frozen
// artifact schemas verbatim rather than restating them, so a procedure's shape
// can never drift from the artifact it produces.
import {
  denormalizedColumnMappingSchema,
  triageSummarySchema,
} from './dataset.js';
import { mappingProposalSchema } from './ports.js';
import { mappingProvenanceSchema } from './mapping.js';
// PE-1991 — the transport ceiling on `validate`. Imported rather than restated
// so the wizard's pre-flight check and this bound cannot drift apart.
import { VALIDATE_MAX_ROWS } from './limits.js';

// The contract surface frozen by `decisions.md#D-TOOL-BLACKBOX` and specified
// in `docs/initiatives/new/dashboard-import-tool/import-engine-contract.md`: five
// outer artifacts and three inner ports. This constitutes Milestone A's exit
// gate (parallelization map § "The gates, precisely", condition 2).
//
//   O1  job record ................. import-job.ts
//   O2  mapped.jsonl ............... dataset.ts
//   O3  MappingConfig .............. mapping.ts (+ the denormalized copy in dataset.ts)
//   O4  TriageSummary .............. dataset.ts
//   O5  funnel events .............. funnel-events.ts
//   P1  ParseProvider .............. ports.ts
//   P2  MappingProvider ............ ports.ts
//   P3  WizardHost ................. ports.ts
//   shared vocabulary .............. fields.ts (TargetFieldKey, SourceFileHint,
//                                    ResolvedConsent + the consent precedence rule)
export * from './import-job.js';
export * from './mapping.js';
export * from './fields.js';
export * from './dataset.js';
export * from './funnel-events.js';
export * from './ports.js';
export * from './template-registry.js';
export * from './template-generate.js';
export * from './limits.js';

// ---------------------------------------------------------------------------
// tRPC procedure I/O — imports.jobs.list
// ---------------------------------------------------------------------------

export const jobsListInputSchema = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(30),
});

/**
 * SPEC §3.1's GSI query — "all jobs for this org, newest first" (the Hub
 * listing, §6.1).
 *
 * `jobs` was `z.array(z.unknown())` while this package was a Stage-1 shell;
 * it is now the real §3.1 job record. That retyping IS the gate deliverable —
 * E1 builds its 13-state badge map and reconciling counts against this.
 */
export const jobsListOutputSchema = z.object({
  jobs: z.array(importJobSchema),
  nextCursor: z.string().optional(),
});

/**
 * CALLER-side input type. Uses `z.input` (not `z.infer`), because `limit` has a
 * `.default(30)`: `z.infer` describes the shape AFTER parsing, where the default
 * has been applied and `limit` is therefore REQUIRED. A caller passing `{}` —
 * the whole point of a default — would fail to compile against the inferred
 * type. `z.input` is the pre-parse shape, where `limit` is optional.
 */
export type JobsListInput = z.input<typeof jobsListInputSchema>;

/**
 * SERVER-side, post-parse input type: `limit` is guaranteed present here, so a
 * procedure body never has to re-apply the default.
 */
export type JobsListInputParsed = z.infer<typeof jobsListInputSchema>;

export type JobsListOutput = z.infer<typeof jobsListOutputSchema>;

// ---------------------------------------------------------------------------
// tRPC procedure I/O — the job lifecycle (PE-1890)
//
// SPEC §4 defines these as REST-shaped bullets (`POST /imports/jobs`) written
// before the tRPC decision; the frozen boundary is the annex's WizardHost
// table (§4.3), which names five procedures and says the wizard is "a client
// of five named procedures, and nothing else." Where §4's prose and the annex
// disagree, the annex wins — it is the artifact `D-TOOL-BLACKBOX` freezes.
//
// The SPEC specifies almost no OUTPUT shapes for these. Where a shape below is
// not quoted from the SPEC it is noted as such, so a reader can tell a
// transcription from a judgment call.
// ---------------------------------------------------------------------------

/**
 * `imports.createJob` — annex §4.3: "job in STAGED + presigned S3 PUT URL".
 *
 * ONE procedure, not two. SPEC §4.1 lists `POST /imports/presigned-url` AND
 * `POST /imports/jobs` as separate calls, but the annex's WizardHost table
 * names a single `imports.createJob` producing both, and caps the wizard at
 * five procedures total. The §4.1 bullets are pre-tRPC prose under §4's
 * "construction is DEFERRED" banner.
 *
 * `jobId` is CLIENT-generated (§3.1: "Client-generated UUID. Serves as the
 * idempotency key — creating a job with the same ID is a no-op"), so it is an
 * input, not a server mint. Note "no-op", not "error": a repeat create returns
 * the existing job successfully.
 *
 * `sourceFileKey` is deliberately NOT an input. Annex §4.3a scopes the
 * presigned URL to a single S3 key; accepting a client-supplied key would let
 * a caller write outside their own job's prefix. The server derives it from
 * §3.7's layout.
 */
export const jobsCreateInputSchema = z.object({
  jobId: z.string().uuid(),
  fileName: z.string().min(1).max(500),
  type: importJobTypeSchema.default('contacts'),
  sourceFileHint: sourceFileHintSchema.optional(),
  expectedRowCount: z.number().int().nonnegative().optional(),
});

/**
 * NOT SPEC-SPECIFIED beyond annex §4.3's "job in STAGED + presigned S3 PUT
 * URL". `uploadUrl` is the presigned PUT; `alreadyExisted` lets the caller
 * distinguish a fresh create from the idempotent replay of one.
 */
export const jobsCreateOutputSchema = z.object({
  job: importJobSchema,
  uploadUrl: z.string().url(),
  uploadExpiresAt: z.string().datetime(),
  alreadyExisted: z.boolean(),
});

/**
 * `imports.commit` — annex §4.3: "attestation recorded, `imports/job.committed`
 * fired".
 *
 * `attested` IS ALLOWED TO BE FALSE. §3.1: "`attested: false` is a valid
 * recorded outcome (declining still imports, §9.1)"; §9.1: "if the user skips
 * the consent attestation, the import still completes — contacts are stored
 * and visible but excluded from marketing sends." §4.4's "(required
 * checkbox)" is UI copy for the interaction, not a server-side validity gate.
 * A server that rejects `attested: false` would break the §9.1 path.
 *
 * `copyVersion` must come from the client because only the client knows which
 * attestation text actually rendered. `actorId` and `timestamp` are server-set
 * — a client-supplied attestation timestamp is not evidence of anything.
 */
export const jobsCommitInputSchema = z.object({
  jobId: z.string().uuid(),
  attested: z.boolean(),
  copyVersion: z.string().min(1),
  /**
   * The SEPARATE SMS attestation (`decisions.md#D-SMS-OPT-IN-GRANT`), and the
   * `smsCopyVersion` that pins which wording rendered.
   *
   * OPTIONAL so every existing caller keeps compiling and keeps its current
   * behavior: omitted means NOT attested, which denies every SMS grant. A
   * client that never sends these can never grant SMS consent — the safe
   * default for a field whose absence used to be the only possible state.
   */
  smsAttested: z.boolean().optional(),
  smsCopyVersion: z.string().min(1).optional(),
});

/** NOT SPEC-SPECIFIED. Returns the job so the Hub can render without a refetch. */
export const jobsCommitOutputSchema = z.object({
  job: importJobSchema,
});

/**
 * `imports.cancel` — ONE procedure, TWO mechanisms, chosen by current status.
 *
 * §3.2: "any pre-COMMITTING state → CANCELLED (clean — nothing was written)"
 * is a real conditional transition. But §3.2 is equally explicit that
 * mid-commit is NOT: "The cancel API cannot stop a Lambda step mid-flight.
 * Instead it sets `cancelRequested: true` on the job record and returns." The
 * workflow observes the flag at a wave boundary, waits for the in-flight wave
 * to settle, and only then writes COMMITTING → CANCELLING.
 *
 * So this procedure MUST NEVER write COMMITTING → CANCELLING itself. Doing so
 * would let a rollback start while that job's own batches are still writing —
 * the exact race the wave-settle rule exists to prevent.
 */
export const jobsCancelInputSchema = z.object({
  jobId: z.string().uuid(),
});

/**
 * NOT SPEC-SPECIFIED. `outcome` distinguishes the two mechanisms because the
 * Hub's copy differs: a pre-commit cancel is done ("Cancelled"), a mid-commit
 * one is a request the workflow will honour at the next wave boundary
 * ("Cancelling…", §6.1).
 */
export const jobsCancelOutputSchema = z.object({
  outcome: z.enum(['cancelled', 'cancel-requested']),
  status: importJobStatusSchema,
});

/**
 * `imports.jobs.reverse` — §6.1's Reverse on a completed import.
 *
 * NOT one of the frozen five (annex §4.3): reversal is a post-commit Hub
 * operation, so it joins `jobs.*` alongside cancel/retry/downloadResults
 * rather than the wizard surface. Strictly additive (annex §8).
 *
 * The procedure only REQUESTS the reversal — it fires
 * `imports/job.reversal-requested` and returns. The SUCCESS → REVERSING
 * transition belongs to the workflow's `load-job` step, which takes it
 * conditionally so a duplicate event or a double-clicked Reverse loses the
 * condition and stops rather than deleting twice. Transitioning here as well
 * would double-write and strip the workflow of the one guard that makes
 * at-least-once delivery safe — the same division of labour `imports.commit`
 * has with the import workflow.
 */
export const jobsReverseInputSchema = z.object({
  jobId: z.string().uuid(),
});

/**
 * NOT SPEC-SPECIFIED. `outcome: 'reversal-requested'` mirrors cancel's
 * `cancel-requested`: the work is asynchronous, so the Hub's copy must promise
 * a reversal in progress ("Reversing…", §6.1) rather than a completed one. The
 * job is returned unchanged (still SUCCESS) so the Hub can re-render without a
 * refetch; it moves to REVERSING when the workflow claims it.
 */
export const jobsReverseOutputSchema = z.object({
  outcome: z.literal('reversal-requested'),
  job: importJobSchema,
});

/** `imports.jobs.get` — single-job read. Input is just the id. */
export const jobsGetInputSchema = z.object({
  jobId: z.string().uuid(),
});

/**
 * The FULL job record, not a projection. §6.2's poller needs `counts` +
 * `status`; annex §4.3 obligation 5 (resume) needs `mappingConfig`,
 * `mappedFileKey` and `triage` to reopen the wizard at the matching step. Since
 * `jobsListOutputSchema` already returns full records, `get` adds no new
 * fields — only single-record access.
 */
export const jobsGetOutputSchema = z.object({
  job: importJobSchema,
});

/**
 * `imports.jobs.retry` — §6.1's Retry: "creates a NEW job pre-seeded with the
 * same source file and confirmed `mappingConfig`, landing the user on the
 * validate step — never a blind re-run."
 *
 * `newJobId` is client-generated for the same reason `createJob`'s is: §3.1
 * makes the job id the idempotency key.
 */
export const jobsRetryInputSchema = z.object({
  sourceJobId: z.string().uuid(),
  newJobId: z.string().uuid(),
});

/**
 * NOT SPEC-SPECIFIED. `uploadUrl` is present only in §6.1's expired branch —
 * "If the source file has already expired from S3, Retry prompts for re-upload
 * and retains the mapping" — so its presence IS the signal to the wizard that
 * a re-upload is required.
 */
export const jobsRetryOutputSchema = z.object({
  job: importJobSchema,
  sourceFileExpired: z.boolean(),
  uploadUrl: z.string().url().optional(),
  uploadExpiresAt: z.string().datetime().optional(),
});

/**
 * `imports.jobs.downloadResults` — NOT one of the six PE-1890 names, and
 * scoped nowhere in the SPEC, but §6.1 renders a download link on SUCCESS /
 * FAILED / CLEANED_UP and a SECOND one on REVERSED ("Original · Reversal"),
 * and E1/E2 build that Hub against a procedure nobody had defined. Added here
 * rather than left for E2 to discover missing.
 *
 * `kind` exists because REVERSED links two different files (§6.1). Gated on
 * `view_contacts`, not `import_contacts`: §2.5 — "results files contain
 * contact PII".
 *
 * Expiry is NOT checked by an S3 call. §6.1 pins the idiom: expiry is
 * "computed from `completedAt`, no S3 call, never a dead link."
 */
export const jobsDownloadResultsInputSchema = z.object({
  jobId: z.string().uuid(),
  kind: z.enum(['IMPORT', 'REVERSAL']).default('IMPORT'),
});

export const jobsDownloadResultsOutputSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string().datetime(),
});

/**
 * `imports.jobs.presignSourceTable` — ADM-35.
 *
 * Mints a FRESH, write-once key for the browser to PUT its derived table to,
 * immediately before each upload. Called again before a re-validate, which is
 * the whole reason it exists: a presigned URL is a capability against exactly
 * ONE server-derived key, and `createJob` runs only once per job (on replay it
 * re-presigns the STORED key), so there was previously no way to obtain a
 * second key mid-job. Without one, a re-validate after an in-grid edit would
 * have to overwrite an object a still-running validate may be reading.
 *
 * DELIBERATELY NOT ON THE FROZEN FIVE. Annex §4.3 freezes the wizard surface at
 * exactly five procedures and `wizard-surface.test.ts` asserts that count, so
 * this is mounted under `jobs.*` like the other non-wizard operations.
 *
 * The caller supplies NO key material. The nonce is minted server-side — same
 * rule as `sourceFileKey`, and SPEC §3.7a explains why a client-supplied key is
 * refused.
 */
export const jobsPresignSourceTableInputSchema = z.object({
  jobId: z.string().uuid(),
});

export const jobsPresignSourceTableOutputSchema = z.object({
  url: z.string().url(),
  /**
   * Echoed back so the caller can pass it to `validate` verbatim rather than
   * re-deriving it — the client never constructs this key itself.
   */
  key: z.string(),
  expiresAt: z.string().datetime(),
});

// Caller-side (`z.input`) vs server-side (`z.infer`) input types are split for
// every schema carrying a `.default()`, for the reason documented on
// JobsListInput above: `z.infer` describes the POST-parse shape, where the
// default is applied and the field is required, so a caller passing `{}` would
// fail to compile.
export type JobsCreateInput = z.input<typeof jobsCreateInputSchema>;
export type JobsCreateInputParsed = z.infer<typeof jobsCreateInputSchema>;
export type JobsCreateOutput = z.infer<typeof jobsCreateOutputSchema>;

export type JobsCommitInput = z.infer<typeof jobsCommitInputSchema>;
export type JobsCommitOutput = z.infer<typeof jobsCommitOutputSchema>;

export type JobsCancelInput = z.infer<typeof jobsCancelInputSchema>;
export type JobsCancelOutput = z.infer<typeof jobsCancelOutputSchema>;

export type JobsReverseInput = z.infer<typeof jobsReverseInputSchema>;
export type JobsReverseOutput = z.infer<typeof jobsReverseOutputSchema>;

export type JobsGetInput = z.infer<typeof jobsGetInputSchema>;
export type JobsGetOutput = z.infer<typeof jobsGetOutputSchema>;

export type JobsRetryInput = z.infer<typeof jobsRetryInputSchema>;
export type JobsRetryOutput = z.infer<typeof jobsRetryOutputSchema>;

export type JobsDownloadResultsInput = z.input<typeof jobsDownloadResultsInputSchema>;
export type JobsDownloadResultsInputParsed = z.infer<typeof jobsDownloadResultsInputSchema>;
export type JobsDownloadResultsOutput = z.infer<typeof jobsDownloadResultsOutputSchema>;

export type JobsPresignSourceTableInput = z.infer<typeof jobsPresignSourceTableInputSchema>;
export type JobsPresignSourceTableOutput = z.infer<typeof jobsPresignSourceTableOutputSchema>;

// ---------------------------------------------------------------------------
// tRPC procedure I/O — the mapping + validation surface (PE-1899)
//
// Annex §4.3's WizardHost table names three of its five procedures here:
//
//   | `imports.proposeMapping` | 2 · Map      | `MappingProposal` (O-adjacent; DISPOSABLE) |
//   | `imports.confirmMapping` | 2 · Map      | `MappingConfig` (**O3**) on the job record  |
//   | `imports.validate`       | 3 · Validate | `mapped.jsonl` (**O2**) + `TriageSummary` (**O4**) |
//
// STRICTLY ADDITIVE (annex §8). Every schema below is NEW; nothing existing is
// renamed, removed, or has its meaning changed. `mappingConfigSchema` in
// mapping.ts is left EXACTLY as it was — see `confirmedColumnMappingSchema`
// below for why O3's durable column list lands as a new sibling field rather
// than as a redefinition of that schema's `columns`.
// ---------------------------------------------------------------------------

/**
 * `imports.proposeMapping` — annex §4.3, step 2.
 *
 * THE INPUT CARRIES HEADERS AND SAMPLES, NOT A FILE KEY. SPEC §5.1: "the
 * backend sends the header plus a small redacted sample (~5 rows with
 * emails/phones masked) to the mapping provider", and §4.2's REST sketch is
 * `POST /imports/jobs/{jobId}/mapping` → "sends headers + sample rows". The
 * file itself is parsed browser-side in a web worker behind `ParseProvider`
 * (§4.2 — "File parsing happens in a web worker"), so the client already holds
 * the parsed headers by the time this is called. This backend has NO
 * server-side parse adapter and deliberately does not grow one here: adding a
 * CSV/XLSX parser to this Lambda is a new architectural artifact, not a
 * fill-in (root CLAUDE.md, Commitment 12 · Principle 4).
 *
 * `sampleRows` are the caller's RAW samples. Redaction is applied SERVER-SIDE,
 * on our side of the port boundary, before anything reaches a provider — INV-5
 * and annex §4.2 obligation 6 make redaction STRUCTURAL: "no adapter is ever
 * the component trusted to redact." Accepting pre-redacted samples from the
 * client would move that trust to the browser, which is exactly the inversion
 * the invariant forbids.
 */
export const proposeMappingInputSchema = z.object({
  jobId: z.string().uuid(),
  /** Verbatim file order (P1 `ParsedTable.headers`) — never normalized. */
  headers: z.array(z.string()).min(1).max(500),
  /**
   * Up to 5 raw sample rows. Cell count is NOT refined against `headers`
   * here: a short row is a ParseProvider concern, and rejecting the whole
   * mapping request over a ragged sample would block a file the manual path
   * could still map.
   */
  sampleRows: z.array(z.array(z.string().nullable())).max(5).default([]),
});

/**
 * NOT SPEC-SPECIFIED beyond annex §4.3's "`MappingProposal` (O-adjacent;
 * disposable)". The proposal itself is the frozen `mappingProposalSchema`
 * (ports.ts); the three fields beside it describe HOW it was produced, which
 * the wizard needs to render the right step-2 screen:
 *
 *   * `tier` — §4.0.1's classification. Tier 0 means every column identity-
 *     mapped from the org template, so the wizard renders a confirmation
 *     summary rather than the mapping grid, and NO LLM call was made.
 *   * `unresolvedHeaders` — the columns the classifier could not resolve, i.e.
 *     the ONLY ones sent to the provider. §5.1's structural note: "the unit of
 *     work is the unresolved columns, not the file."
 *   * `manualMappingRequired` — the caller MUST render the manual mapping UI.
 *     True on the degraded path (annex §4.2 obligation 5: "`degraded: true` is
 *     a SUCCESS response, not an error") AND on the duplicate-header path,
 *     where the port cannot faithfully answer at all. Both are successes with
 *     a manual fallback, never request failures — "AI assists but never gates."
 *
 * `provenance` is the durable summary the caller passes back to
 * `confirmMapping`. It exists because `MappingConfig.columns` drops confidence
 * once confirmed, and SPEC §3.8's per-column confidence distribution would
 * otherwise have no home (`decisions.md#D-FUNNEL-TRANSPORT`).
 */
export const proposeMappingOutputSchema = z.object({
  proposal: mappingProposalSchema,
  /** §4.0.1's tier. `2` is unrepresentable in V1 (D-FINGERPRINT-DEFERRAL). */
  tier: z.union([z.literal(0), z.literal(1), z.literal(3)]),
  /** Columns the tier classifier resolved from the org template, if any. */
  resolvedHeaders: z.array(
    z.object({
      header: z.string(),
      target: columnTargetSchema,
      requiresConsentConfirmation: z.boolean(),
    }),
  ),
  /** The columns actually sent to the provider — never the whole file at Tier 0/1. */
  unresolvedHeaders: z.array(z.string()),
  /** True ⇒ the caller MUST render the manual mapping UI. */
  manualMappingRequired: z.boolean(),
  /**
   * Present when the request could not be answered totally — today only
   * `DUPLICATE_HEADERS`. Display-only; a caller renders it and falls back.
   */
  manualMappingReason: z.enum(['DEGRADED', 'DUPLICATE_HEADERS']).optional(),
  /** The duplicated headers, when `manualMappingReason === 'DUPLICATE_HEADERS'`. */
  duplicateHeaders: z.array(z.string()).optional(),
  provenance: mappingProvenanceSchema.optional(),
});

/**
 * One confirmed column of O3, as the user decided it.
 *
 * WHY A NEW SCHEMA RATHER THAN A CHANGE TO `mappingConfigSchema.columns`.
 * That field is `Record<header, target|null>` — it cannot express three things
 * the annex's O3 conformance bullet requires ("totality over columns · explicit
 * `SKIP` · single-valued-target uniqueness · `origin` present on every
 * column"): it has no `columnIndex` (so duplicate headers collapse, exactly the
 * defect the OpenRouter adapter escalated), no `origin` (so §3.8's override
 * rate is not computable from DynamoDB), and no place for a `transform`.
 * Redefining it would be a BREAKING seam change requiring a §8 escalation.
 * Adding this array as a new optional sibling is additive and costs nothing —
 * and it is field-for-field the same shape as
 * `denormalizedColumnMappingSchema`, which is what O2's header line already
 * carries, so the two cannot drift.
 */
export const confirmedColumnMappingSchema = denormalizedColumnMappingSchema;
export type ConfirmedColumnMapping = z.infer<typeof confirmedColumnMappingSchema>;

/**
 * `imports.confirmMapping` — annex §4.3, step 2: "`MappingConfig` (**O3**) on
 * the job record".
 *
 * TOTALITY IS ENFORCED SERVER-SIDE, not asserted here: a Zod refinement cannot
 * see the file's header list, which lives on the parse the client holds. What
 * the schema CAN pin is that every column names its own position and origin,
 * and that a skip is the explicit `SKIP` sentinel rather than an absent key —
 * annex §5: "Absence is NEVER a way to express a decision."
 *
 * `expectedHeaders` is what makes the server-side totality check possible. It
 * is the file's verbatim header list, and the procedure refuses a config that
 * does not cover it exactly, one column per header, in position order.
 */
export const confirmMappingInputSchema = z.object({
  jobId: z.string().uuid(),
  /** Verbatim file headers, in file order — the totality check's reference. */
  expectedHeaders: z.array(z.string()).min(1).max(500),
  /** One entry per source column. `SKIP` is explicit; absence is illegal. */
  columns: z.array(confirmedColumnMappingSchema).min(1).max(500),
  /** §5.3's file-level default region for bare national phone numbers. */
  defaultPhoneRegion: z
    .string()
    .trim()
    .toUpperCase()
    .pipe(
      z
        .string()
        .regex(/^[A-Z]{2}$/, 'must be a two-letter ISO-3166-1 alpha-2 country code'),
    )
    .optional(),
  /**
   * Annex §4.1b — a consent input of EQUAL standing to a column (SPEC §9.3).
   * Recorded on the job at create; re-supplying it here lets the user correct
   * a mis-declared file role without restarting the wizard.
   */
  sourceFileHint: sourceFileHintSchema.optional(),
  /** §5.2's detected source, retained so the import stays reproducible. */
  detectedSource: z.string().nullable().optional(),
  /** SPEC §3.8's confidence distribution, from `proposeMapping`. */
  provenance: mappingProvenanceSchema.optional(),
});

/**
 * NOT SPEC-SPECIFIED. Returns the job so the wizard advances without a
 * refetch, plus the stored config so a caller can render exactly what was
 * persisted rather than what it believes it sent — the two differ whenever the
 * server attached a transform the client did not (a consent column's
 * `CONSENT_ENUM_MAP`, below).
 */
export const confirmMappingOutputSchema = z.object({
  job: importJobSchema,
  columns: z.array(confirmedColumnMappingSchema),
});

/**
 * `imports.validate` — annex §4.3, step 3: "`mapped.jsonl` (**O2**) +
 * `TriageSummary` (**O4**)".
 *
 * A DISCRIMINATED UNION ON `source` SINCE ADM-35, because the rows now have two
 * possible transports and exactly one may be used per request:
 *
 *   - `'body'` — the original: every row rides in the POST body. Bounded by the
 *     shared ApiRouter's Lambda@Edge signer at an exact 1 MiB, which refuses an
 *     ordinary nonprofit export at roughly 4,000–6,000 rows. Retained for one
 *     release so a tab open on the old bundle keeps working.
 *   - `'s3'` — the browser PUTs its derived table to a server-minted key (O6)
 *     and names it here. The body carries only metadata, so file size stops
 *     being bounded by the request at all.
 *
 * EITHER WAY THE ROWS ARE UNTRUSTED. The deterministic core (transforms,
 * validation, consent resolution, O2 authorship) runs over them unchanged and
 * entirely server-side — precisely the arrangement annex §4.3a sanctions for a
 * vendor that returns rows ("its rows are consumed as an untrusted
 * `ParsedTable` … our deterministic core runs over them UNCHANGED"). Moving the
 * bytes through S3 changes the transport, not who decides. The four outcomes
 * §4.3a keeps ours stay ours.
 *
 * `headers` travels on BOTH arms. On the body arm it is what the O3 join is
 * performed against; on the S3 arm the server additionally asserts the object's
 * own header line equals it, because a disagreement between the two would join
 * the object's cells against the wrong column list and write every value into
 * the wrong field.
 *
 * BOTH ARMS ARE `.strict()`. Zod STRIPS unknown keys by default, and that
 * default is a live hazard during the dual-path release: a new-bundle tab
 * posting `{source: 's3', sourceKey}` at a backend that predates this union
 * would have BOTH fields silently discarded and degrade to an empty-rows
 * request rather than failing. `.strict()` plus the `rows` floor makes that
 * structurally impossible instead of deploy-order dependent.
 */
const validateInputCommon = {
  jobId: z.string().uuid(),
  /** Verbatim file headers, in file order. Must match the confirmed O3. */
  headers: z.array(z.string()).min(1).max(500),
  /** §9.1's commit-screen default, when the user has already chosen one. */
  attestationDefault: attestationDefaultSchema.optional(),
};

export const validateInputBodySchema = z
  .object({
    ...validateInputCommon,
    /**
     * Legacy transport. `.default('body')` keeps a caller that predates the
     * union — the old bundle, the QA harness, a script — valid without change,
     * which is what makes this a non-breaking contract edit.
     */
    source: z.literal('body').default('body'),
    /**
     * Every DATA row, in file order — not a preview. Width must equal
     * `headers.length` on every row (P1 obligation 1: the parser pads with
     * `null`, it never shifts cells left).
     *
     * BOUNDED SINCE PE-1991. On this arm the real ceiling is request-body BYTES
     * (~1MB at the Lambda@Edge signer), not row count — see `limits.ts`. The
     * byte check lives in the procedure because Zod cannot measure the
     * serialized payload; this `.max()` is the cheap backstop that refuses a
     * pathological caller before the deterministic core allocates for it.
     *
     * FLOORED AT 1 SINCE ADM-35. An empty set used to be accepted end-to-end
     * and landed a READY job with a header-only `mapped.jsonl` and
     * `counts.total === 0` — "0 imported", with nothing anywhere objecting.
     */
    rows: z
      .array(z.array(z.string().nullable()))
      .min(1, { message: 'An import needs at least one data row.' })
      .max(VALIDATE_MAX_ROWS, {
        message: `An import cannot exceed ${VALIDATE_MAX_ROWS.toLocaleString('en-US')} rows in one file.`,
      }),
  })
  .strict();

export const validateInputS3Schema = z
  .object({
    ...validateInputCommon,
    source: z.literal('s3'),
    /**
     * The object written in the upload step, as returned by
     * `jobs.presignSourceTable`.
     *
     * NAMED, NOT DERIVED, because the nonce is server-minted per upload and the
     * server cannot recompute which one this request means. It is NOT trusted:
     * the read path re-validates that the key sits under this job's prefix (or
     * the prefix of the job it was retried from) before fetching anything.
     */
    sourceKey: z.string().min(1),
  })
  .strict();

/**
 * ⚠️ `z.preprocess` + `z.union`, NOT `z.discriminatedUnion` — and this is a
 * correctness fix, not a style choice.
 *
 * `z.discriminatedUnion` reads the discriminator BEFORE any arm's defaults are
 * applied, so a payload that omits `source` matches NO arm and fails with
 * `invalid_union_discriminator` — even though the body arm declares
 * `.default('body')`. Verified against zod 3 directly, not assumed.
 *
 * That would break the dual-path rollout outright: EVERY existing caller omits
 * `source` (the deployed dashboard bundle, the QA harness, any script), so the
 * new backend would refuse every request from the old frontend — the exact
 * deploy-order failure this union exists to make safe. Defaulting the
 * discriminator first is what keeps the legacy shape valid.
 *
 * The cost is a slightly worse error message on a malformed payload (a union
 * error listing both arms rather than one discriminator error). That is a fair
 * trade for not breaking every in-flight caller, and the arms are `.strict()`
 * so a genuinely wrong shape is still refused.
 */
export const validateInputSchema = z.preprocess(
  (value) =>
    typeof value === 'object' && value !== null && !Array.isArray(value)
      ? { source: 'body', ...(value as Record<string, unknown>) }
      : value,
  z.union([validateInputBodySchema, validateInputS3Schema]),
);

/**
 * NOT SPEC-SPECIFIED beyond annex §4.3's two artifacts. `mappedFileKey` is
 * surfaced because §3.1 stores it on the job record and a re-validate REPOINTS
 * it at a new revision — returning it lets a caller confirm which revision its
 * triage describes rather than assuming `mapped.jsonl`.
 */
export const validateOutputSchema = z.object({
  job: importJobSchema,
  triage: triageSummarySchema,
  /** The S3 key just written. Revision 2+ is `mapped-2.jsonl`, `mapped-3.jsonl`, … */
  mappedFileKey: z.string(),
  /**
   * O4's own reconciliation verdict (`total === ready + excluded`,
   * `ready === willCreate + willMerge`). Surfaced rather than thrown, per the
   * annex's visible-error-state rule — the wizard RENDERS a failed
   * reconciliation instead of the user meeting a 500.
   */
  reconciles: z.boolean(),
  reconciliationDetail: z.string().optional(),
});

export type ProposeMappingInput = z.input<typeof proposeMappingInputSchema>;
export type ProposeMappingInputParsed = z.infer<typeof proposeMappingInputSchema>;
export type ProposeMappingOutput = z.infer<typeof proposeMappingOutputSchema>;

export type ConfirmMappingInput = z.infer<typeof confirmMappingInputSchema>;
export type ConfirmMappingOutput = z.infer<typeof confirmMappingOutputSchema>;

export type ValidateInput = z.infer<typeof validateInputSchema>;
export type ValidateOutput = z.infer<typeof validateOutputSchema>;
